import { ChevronRight, type LucideIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, NavLink, matchPath, useLocation, useMatch } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isRouteAuthorized, type AppRoute } from "@/lib/routes";
import { useOptionalAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

/**
 * Row-rendering primitives shared by every sidebar (`Sidebar.tsx` for Sales,
 * `GlobalSidebar.tsx` for the launcher-level Global area). Extracted so both
 * sidebars render identical nav rows/active-states/collapse behavior — only
 * the surrounding chrome (which sections, which route registry, the brand
 * link destination) differs between them, and that difference lives in each
 * sidebar's own file, not here.
 */

/** PowerUp brand mark — chevron above a "U". Two-tone via the
 *  `--brand-accent-hover` (darker) and `--brand-accent` (mint) theme
 *  tokens so the mark recolors with the active theme/palette rather
 *  than being baked to a single set of RGB values. Used in the
 *  icons-mode rail in place of the full-width wordmark that doesn't
 *  fit at 64px. */
export function Logomark({ size = 28 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 282 282.2"
      aria-hidden
    >
      <path
        d="M228.8 58.7v-7c0-1.1-.4-1.7-1.3-2.3-2.9-1.7-5.9-3.4-8.8-5-25.4-14.7-50.8-29.3-76.1-44-.6-.3-1.1-.5-1.6-.4-.5-.1-1 .1-1.6.4-25.4 14.7-50.7 29.3-76.1 44-2.9 1.7-5.8 3.4-8.8 5-.9.5-1.3 1.2-1.3 2.3v43.9s7.2-4.2 10.1-5.9c25.4-14.7 50.8-29.3 76.1-44 .6-.3 1.1-.5 1.6-.4.5-.1 1 .1 1.6.4 25.4 14.7 50.8 29.3 76.1 44 2.9 1.7 9.6 5.7 10.1 6.3V58.7z"
        style={{ fill: "var(--brand-accent-hover)" }}
      />
      <path
        d="M98.4 191.1c0 16.5 3.4 29.6 10.2 39.4 6.8 9.8 17.6 14.6 32.3 14.6 14.8 0 25.6-4.9 32.4-14.6 6.7-9.8 10.1-22.9 10.1-39.4V108h43.7v88.7c0 17-3.4 32-10.1 44.8-6.7 12.9-16.5 22.9-29.3 30s-28.4 10.7-46.8 10.7c-18.3 0-33.8-3.6-46.6-10.7s-22.5-17.1-29.3-30c-6.7-12.9-10.1-27.8-10.1-44.8V108h43.5v83.1z"
        style={{ fill: "var(--brand-accent)" }}
      />
    </svg>
  );
}

/** Top-level sidebar entry that links to a single route. The default for
 *  routes without `subItems`. Stub routes render as inert.
 *
 *  Active state is computed via `useMatch` rather than NavLink's
 *  function-className callback. That callback doesn't survive Radix's
 *  `TooltipTrigger asChild` slot in icons-mode — Slot serializes the
 *  function to a string when it merges props, so every link ends up with
 *  the active class baked in literal. Computing `isActive` here and
 *  passing a regular string className side-steps the issue without
 *  losing nested-route awareness.
 *
 *  `end` is `false` only when the route owns real nested children
 *  (`item.children` — a tabbed detail page whose URL grows with `/tab`);
 *  otherwise it's `true` for an EXACT match. A flat leaf whose own path
 *  happens to be a literal prefix of an unrelated sibling's path — Market
 *  Intelligence's Overview sits at the module root `/market-intelligence`,
 *  which every other page in the module's URL also starts with — lit up
 *  as "active" on every one of those sibling pages under the old
 *  unconditional `end: false`, alongside whichever row was genuinely
 *  current. Two filled rows for one location is the bug this guards
 *  against; only a route that actually extends its own path needs the
 *  loose match — real `children`, or `activeOnSubpaths` for deeper pages
 *  that are separate routes (the RFQ walkthrough under the RFQs row). */
export function NavLeaf({
  item,
  onClose,
  compact,
}: {
  item: AppRoute;
  onClose: () => void;
  compact: boolean;
}) {
  const Icon = item.icon;
  const match = useMatch({
    path: item.path,
    end: !item.children?.length && !item.activeOnSubpaths,
  });
  const { pathname } = useLocation();
  const isActive = !!match || matchesAlsoActiveOn(item, pathname);
  const baseClass = compact
    ? "flex h-10 w-10 items-center justify-center rounded-md transition-colors"
    : "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors";

  if (item.status === "stub") {
    const inner = (
      <button
        type="button"
        title={compact ? undefined : "Not yet ported"}
        aria-label={compact ? item.label : undefined}
        className={cn(
          baseClass,
          !compact && "w-full",
          "text-left text-muted-foreground opacity-60 hover:bg-row-hover",
        )}
      >
        {Icon && <Icon size={compact ? 20 : 16} strokeWidth={1.75} />}
        {!compact && <span>{item.label}</span>}
      </button>
    );
    return compact ? (
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent side="right">{item.label} (not yet ported)</TooltipContent>
      </Tooltip>
    ) : (
      inner
    );
  }

  const link = (
    <NavLink
      to={item.path}
      onClick={onClose}
      // A cross-link leaves this module for a page another one owns, so it
      // opens beside the reader's place rather than replacing it. `rel` is
      // required with `_blank`: without it the opened page gets a handle on
      // this one through `window.opener`.
      target={item.crossLink ? "_blank" : undefined}
      rel={item.crossLink ? "noreferrer" : undefined}
      aria-label={compact ? item.label : undefined}
      className={cn(
        baseClass,
        isActive
          ? compact
            ? "bg-primary-dim text-primary"
            : "bg-nav-active-bg font-semibold text-primary"
          : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
      )}
    >
      {Icon && <Icon size={compact ? 20 : 16} strokeWidth={1.75} />}
      {!compact && <span>{item.label}</span>}
    </NavLink>
  );

  return compact ? (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  ) : (
    link
  );
}

/** Top-level sidebar entry with nested children. Clicking the parent
 *  toggles expand/collapse — it does NOT navigate. Auto-expands when
 *  the current URL matches one of its children so deep-linking always
 *  shows the active child in context. Collapse state persists in
 *  localStorage so explicit closes survive reloads.
 *
 *  Active state on the parent: highlighted (no underline) when any
 *  child is the active route, so the user keeps orientation when
 *  scrolled. Hover state on the row regardless of expansion.
 *
 *  In icons-mode the expander is replaced by a single icon button that
 *  navigates to the first child route — the flyout/expand affordance
 *  doesn't fit a 64px rail and adding a popover here would mean a
 *  second, parallel UX. Treat icons-mode as "drill in to expand" via
 *  the destination page. */
export function NavGroup({
  parent,
  onClose,
  compact,
}: {
  parent: AppRoute;
  onClose: () => void;
  compact: boolean;
}) {
  const Icon = parent.icon;
  const location = useLocation();

  const subItems = useMemo(() => parent.subItems ?? [], [parent.subItems]);
  const hasActiveChild = useMemo(
    () => subItems.some((child) => isItemActive(location.pathname, child)),
    [subItems, location.pathname],
  );

  // A group can also be a page. Market Intelligence's Hubs row is both the
  // region's hub table and the parent of its hubs; Settings is a pure
  // expander with nothing to navigate to. `element` is what tells them apart.
  const isPage = Boolean(parent.element);
  // An EXACT match, not a prefix one: /regions/pjm/hubs is the hub table,
  // /regions/pjm/hubs/pjm_west/forwards is a different page. Prefix matching
  // here filled the parent as well as the leaf, which is the stacked-
  // highlight problem this rule exists to prevent.
  const selfActive = isPage && location.pathname === parent.path;
  // A page that shows this group's contents without being one of them (a
  // region's All view, for its Hubs and Load zones) opens it unlit.
  const openHere = parent.openOn?.includes(location.pathname) ?? false;

  const storageKey = `sidebar:group:${parent.path}`;
  const [explicitlyOpen, setExplicitlyOpen] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === "true") return true;
      if (raw === "false") return false;
      return null;
    } catch {
      return null;
    }
  });

  // A collapse clicked on a page that opens this group on its own holds on that
  // page. Without it the group could not be closed there at all: the page's
  // own reason for opening it (below) outvoted the click, so a region's All
  // view could never collapse its Hubs or Load zones. Kept per page, not in
  // storage, so the next page still reveals where you are.
  const [closedOn, setClosedOn] = useState<string | null>(null);

  // Open if (a) the URL is on a child of this group, (b) the group is itself
  // the page you are on, (c) the page opens it (`openOn`), or (d) the user
  // explicitly opened it — unless the user closed it on this page. Deep-
  // linking reveals where you are without overwriting the collapse
  // preference — navigate elsewhere and the group returns to closed.
  const open =
    closedOn !== location.pathname &&
    (hasActiveChild || selfActive || openHere || explicitlyOpen === true);

  const toggle = () => {
    const next = !open;
    setExplicitlyOpen(next);
    setClosedOn(next ? null : location.pathname);
    try {
      window.localStorage.setItem(storageKey, String(next));
    } catch {
      /* ignore */
    }
  };

  if (compact) {
    const target = isPage ? parent.path : subItems[0]?.path;
    if (!target) return null;
    const link = (
      <NavLink
        to={target}
        onClick={onClose}
        aria-label={parent.label}
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
          hasActiveChild || selfActive
            ? "bg-primary-dim text-primary"
            : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
        )}
      >
        {Icon && <Icon size={20} strokeWidth={1.75} />}
      </NavLink>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{parent.label}</TooltipContent>
      </Tooltip>
    );
  }

  const baseClass =
    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors";

  // ONE filled row per sidebar marks the page you are on. An ancestor of that
  // page gets teal text and nothing else — its open chevron and the child's
  // indent already say "you are inside this". Filling every level instead
  // produced three stacked highlights competing for the same meaning, which
  // is the thing every mature tree nav (VS Code, Linear, GitHub) avoids.
  const rowClass = cn(
    baseClass,
    selfActive
      ? "bg-nav-active-bg font-semibold text-primary"
      : hasActiveChild
        ? "font-semibold text-primary hover:bg-row-hover"
        : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
  );

  const chevron = (
    <ChevronRight
      size={14}
      strokeWidth={1.75}
      className={cn("transition-transform", open ? "rotate-90" : "rotate-0")}
    />
  );

  // How many rows the group holds, after its label — the same "Hubs 16" the
  // region page's filter tabs show. Only while collapsed: open, the rows
  // themselves say how many. Opt-in (`showCount`): a count on Settings would
  // say nothing. Hidden from assistive tech so the row's name stays its label.
  const count = parent.showCount && !open && (
    <span aria-hidden className="ml-1.5 text-xs font-normal tabular-nums text-text-dim">
      {subItems.length}
    </span>
  );

  const children = open && (
    <ul className="mt-0.5 flex flex-col gap-0.5 pl-7">
      {subItems.map((child) => (
        <li key={child.path}>
          <NavSubLeaf item={child} onClose={onClose} />
        </li>
      ))}
    </ul>
  );

  // A group that is also a page needs both behaviours on one row: the label
  // navigates, a separate chevron button expands. Nesting a button inside a
  // link would be invalid HTML and would swallow the navigation.
  if (isPage) {
    return (
      <div>
        <div className="flex items-center gap-0.5">
          <NavLink to={parent.path} onClick={onClose} className={rowClass}>
            {Icon && <Icon size={16} strokeWidth={1.75} />}
            <span className="flex-1 text-left">
              {parent.label}
              {count}
            </span>
          </NavLink>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${parent.label}`}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-row-hover hover:text-foreground"
          >
            {chevron}
          </button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div>
      <button type="button" onClick={toggle} aria-expanded={open} className={rowClass}>
        {Icon && <Icon size={16} strokeWidth={1.75} />}
        <span className="flex-1 text-left">
          {parent.label}
          {count}
        </span>
        {chevron}
      </button>
      {children}
    </div>
  );
}

/** Indented sub-item under a NavGroup. Same active/hover states as
 *  NavLeaf but a tighter row height and smaller icon — visually
 *  subordinate to the parent.
 *
 *  A sub-item carrying `subItems` of its own renders them beneath it,
 *  revealed only while the URL is on or inside it — that is how Market
 *  Intelligence gets Hubs, then SP15, then Forwards without a third row
 *  component. Deliberately not the group's remembered-collapse behaviour:
 *  seven hubs each holding two rows would bury the region's other surfaces. */
export function NavSubLeaf({ item, onClose }: { item: AppRoute; onClose: () => void }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const baseClass =
    "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors";

  const TrailingIcon = item.trailingIcon;
  // Nested rows are gated like every other nav row. The sections above filter
  // only their own level, so without this a row deeper down — Market
  // Intelligence's Curve, under a hub's Forwards — would show to a caller its
  // route turns away. No session (a bare render) keeps every row.
  const session = useOptionalAuthSession();
  const children = useMemo(
    () =>
      (item.subItems ?? []).filter(
        (c) =>
          !session ||
          isRouteAuthorized(
            c,
            session.is_super_admin,
            session.features,
            session.permissions,
            session.is_supplier,
          ),
      ),
    [item.subItems, session],
  );
  // `alsoActiveOn` on a row WITH children marks it as an ancestor of the page
  // (teal, expanded) — the page it names is under one of those children, and
  // filling this row too would stack two highlights. On a leaf it fills the
  // row, below.
  const childActive =
    children.some((c) => isItemActive(pathname, c)) ||
    (children.length > 0 && matchesAlsoActiveOn(item, pathname));
  const alsoFilled = children.length === 0 && matchesAlsoActiveOn(item, pathname);

  // A label, not a destination — it divides the rows beneath it from the ones
  // above. Market Intelligence uses it to separate a region's forward-quoted
  // hubs from its settlement-only points, matching how the page groups them.
  //
  // Rendered as a plain element with no `to`: it is not focusable, not
  // clickable, and carries no `path` the router would ever match.
  if (item.status === "heading") {
    return (
      <div
        className={cn(
          baseClass,
          "pointer-events-none select-none pt-3 text-[0.6875rem] font-semibold uppercase tracking-eyebrow text-text-dim",
        )}
      >
        {item.label}
      </div>
    );
  }

  if (item.status === "stub") {
    return (
      <button
        type="button"
        title="Not yet ported"
        className={cn(
          baseClass,
          "w-full text-left text-muted-foreground opacity-60 hover:bg-row-hover",
        )}
      >
        {Icon && <Icon size={13} strokeWidth={1.75} />}
        <span>{item.label}</span>
      </button>
    );
  }

  return (
    <>
      <NavLink
        to={item.path}
        onClick={onClose}
        end
        className={({ isActive }) =>
          cn(
            baseClass,
            // Same one-highlight rule as NavGroup: filled only when this row
            // IS the page. An ancestor gets teal text; `end` stops the link
            // claiming active status on behalf of its own descendants.
            isActive || alsoFilled
              ? "bg-nav-active-bg font-semibold text-primary"
              : childActive
                ? "font-semibold text-primary hover:bg-row-hover"
                : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
          )
        }
      >
        {Icon && <Icon size={13} strokeWidth={1.75} />}
        <span className="truncate">{item.label}</span>
        {/* Trailing, so it reads as a note about the row rather than as what
            kind of row it is. `ml-auto` pins it to the far edge so the markers
            line up down the list instead of floating at each label's end. */}
        {TrailingIcon && (
          <TrailingIcon
            size={11}
            strokeWidth={2}
            className="ml-auto shrink-0 text-brand-accent"
            aria-hidden
          />
        )}
      </NavLink>
      {childActive && (
        <ul className="mt-0.5 flex flex-col gap-0.5 pl-5">
          {children.map((child) => (
            <li key={child.path}>
              <NavSubLeaf item={child} onClose={onClose} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** True when `pathname` is at or under `target`. Handles both exact
 *  matches (`/settings/feature-flags` === `/settings/feature-flags`)
 *  and trailing-segment matches (so `/settings/feature-flags/edit`
 *  also counts as being on the feature-flags page). */
export function isPathActive(pathname: string, target: string): boolean {
  if (pathname === target) return true;
  return pathname.startsWith(`${target}/`);
}

/** Full NorthStar wordmark, rendered above the `ModuleSwitcher` in every
 *  sidebar's expanded (non-compact) header — a plain link, not an
 *  interactive control beyond that, so it reads as brand chrome rather
 *  than competing with the switcher button right below it for "what do I
 *  click to navigate" attention. `to` is the sidebar's "home" — every shell
 *  passes `/dashboard`, the dashboard-first launcher/home, not a per-shell
 *  page. Light/dark variants swap via the same `[html.light_&]` pattern the
 *  launcher page uses. Compact mode doesn't render this at all —
 *  `SidebarBrandLink`'s compact branch (the small logomark) is the "go
 *  home" affordance there instead; there's no room for a full wordmark at
 *  64px. */
export function SidebarFullLogo({ to }: { to: string }) {
  return (
    <Link to={to} aria-label="PowerUp" className="block px-4 pt-5">
      <img
        src={`${import.meta.env.BASE_URL}logo-full-dark.svg`}
        alt="PowerUp"
        className="block h-auto w-[120px] max-w-full [html.light_&]:hidden"
      />
      <img
        src={`${import.meta.env.BASE_URL}logo-full-light.svg`}
        alt="PowerUp"
        className="hidden h-auto w-[120px] max-w-full [html.light_&]:block"
      />
    </Link>
  );
}

/** Brand link at the top of a sidebar. `to` is the sidebar's "home" —
 *  every shell passes `/dashboard` (the dashboard-first launcher/home).
 *  `appLabel`/`appIcon` name the shell the sidebar belongs to (e.g. "Sales",
 *  the `Briefcase` icon from `LAUNCHER_MODULES`) — shown next to the brand
 *  mark in expanded mode so the sidebar reads as "you're inside the Sales
 *  app," matching the
 *  identity (icon + name) the user picked on the launcher, not just "inside
 *  NorthStar." Compact mode shows only the brand mark regardless — there's
 *  no room for a label at 64px, and the mark alone is the established
 *  "go home" affordance there. */
export function SidebarBrandLink({
  to,
  compact,
  appLabel,
  appIcon: AppIcon,
}: {
  to: string;
  compact: boolean;
  appLabel: string;
  appIcon: LucideIcon;
}) {
  return compact ? (
    <div className="flex flex-col items-center px-2 pt-4 pb-3">
      <Link to={to} aria-label="PowerUp" className="flex h-10 w-10 items-center justify-center">
        <Logomark size={28} />
      </Link>
    </div>
  ) : (
    <div className="relative flex items-center px-4 pt-5 pb-4">
      <Link
        to={to}
        className="flex items-center gap-2.5 no-underline"
        aria-label={`PowerUp — ${appLabel}`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-dim">
          <Logomark size={18} />
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <AppIcon size={16} strokeWidth={1.75} className="shrink-0 text-muted-foreground" />
          <span className="truncate text-base font-semibold text-foreground">{appLabel}</span>
        </span>
      </Link>
    </div>
  );
}

/** Whether one of `item`'s `alsoActiveOn` patterns matches `pathname`
 *  (react-router patterns, prefix-matched). Matched with `matchPath`, not
 *  `useMatch`, so the number of patterns can't change the hook count. */
export function matchesAlsoActiveOn(item: AppRoute, pathname: string): boolean {
  return (
    item.alsoActiveOn?.some((path) => matchPath({ path, end: false }, pathname) !== null) ??
    false
  );
}

/** `isPathActive` for a nav item, counting its `alsoActiveOn` patterns — what
 *  a parent asks of a child when deciding whether the page is under it. */
export function isItemActive(pathname: string, item: AppRoute): boolean {
  return isPathActive(pathname, item.path) || matchesAlsoActiveOn(item, pathname);
}
